import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { MapPin, Globe } from "@phosphor-icons/react";

const FLAG = (cc) => {
    if (!cc || cc.length !== 2) return "🌍";
    const A = 0x1f1e6;
    const a = "A".charCodeAt(0);
    return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => A + (c.charCodeAt(0) - a)));
};

function relTime(iso, t) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return "";
    const sec = Math.max(1, Math.floor(ms / 1000));
    if (sec < 60) return t("flight.ago_seconds", { n: sec });
    const m = Math.floor(sec / 60);
    if (m < 60) return t("flight.ago_minutes", { n: m });
    const h = Math.floor(m / 60);
    return t("flight.ago_hours", { n: h });
}

/**
 * Cards-in-flight widget. Renders the most recent (de-duplicated) places
 * Zcardon cards have *landed* in the last 48h.
 *
 * We deliberately show the **receiver** side only — the sender side is
 * ambiguous (cluster login IPs) and faking it would feel dishonest. The
 * map glyph + flag emoji do most of the visual lifting; the actual data
 * is just a line list.
 *
 * Renders nothing when:
 *  - the API call fails
 *  - we have fewer than 1 distinct location (fresh deploy)
 */
export default function CardsInFlight({ theme = "light", standalone = true }) {
    const t = useT();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let live = true;
        const load = () =>
            api
                .get("/public/cards-in-flight")
                .then((r) => { if (live) setData(r.data); })
                .catch(() => { if (live) setError(true); });
        load();
        const ref = setInterval(load, 90_000); // backend caches 60s anyway
        return () => { live = false; clearInterval(ref); };
    }, []);

    if (error) return null;
    if (!data) return null;
    const locs = (data.locations || []).slice(0, 6);
    if (locs.length === 0) return null;

    const dark = theme === "dark";
    const sectionCls = standalone
        ? `border-t ${dark ? "border-white/10 bg-[#0A1F16]" : "border-[#E5E0D8] bg-white"} py-10`
        : "py-2";
    const eyebrowCls = dark ? "text-white/70" : "text-[#4A5C50]";
    const eyebrowIcon = dark ? "text-[#CBA35C]" : "text-[#184A30]";
    const titleCls = dark ? "text-[#FBF9F6]" : "text-[#112318]";
    const subtitleCls = dark ? "text-white/60" : "text-[#4A5C50]";
    const dividerCls = dark ? "divide-white/10 border-white/10" : "divide-[#E5E0D8] border-[#E5E0D8]";
    const pinCls = dark ? "text-[#CBA35C]" : "text-[#184A30]";
    const labelCls = dark ? "text-[#FBF9F6]" : "text-[#112318]";
    const tsCls = dark ? "text-white/55" : "text-[#4A5C50]";
    const moreCls = dark ? "text-white/55" : "text-[#4A5C50]";

    return (
        <section className={sectionCls} data-testid="cards-in-flight">
            <div className={standalone ? "max-w-6xl mx-auto px-6" : ""}>
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-4">
                        <div className={`flex items-center gap-2 ${eyebrowCls}`}>
                            <Globe size={16} weight="duotone" className={eyebrowIcon} />
                            <p className="text-[11px] tracking-[0.3em] uppercase">{t("flight.eyebrow")}</p>
                        </div>
                        <h3 className={`serif text-3xl sm:text-4xl ${titleCls} mt-3 leading-tight`}>
                            {t("flight.title")}
                        </h3>
                        <p className={`mt-3 text-sm ${subtitleCls} leading-relaxed`}>
                            {t("flight.subtitle")}
                        </p>
                    </div>

                    <div className="lg:col-span-8">
                        <ul className={`divide-y ${dividerCls} border-y`}>
                            {locs.map((l, i) => (
                                <li
                                    key={`${l.label}-${l.ts}`}
                                    className="flex items-center gap-4 py-3.5 fade-in"
                                    style={{ animationDelay: `${i * 60}ms` }}
                                    data-testid={`cards-in-flight-row-${i}`}
                                >
                                    <span className="text-2xl leading-none w-8 text-center" aria-hidden>
                                        {FLAG(l.country_code)}
                                    </span>
                                    <div className="flex-1 min-w-0 flex items-center gap-2">
                                        <MapPin size={16} weight="duotone" className={`${pinCls} shrink-0`} />
                                        <p className={`serif text-lg ${labelCls} truncate`}>
                                            {t("flight.row_template", { label: l.label })}
                                        </p>
                                    </div>
                                    <p className={`mono text-[11px] tracking-wider ${tsCls} shrink-0`}>
                                        {relTime(l.ts, t)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                        {data.distinct_count > locs.length && (
                            <p className={`mt-3 text-[12px] ${moreCls}`}>
                                {t("flight.more_count", { n: data.distinct_count - locs.length })}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
